import React from 'react';

export const apiKeys = {
  id: 'settings-api-keys',
  title: 'Managing API Keys',
  description: 'How to create, revoke, and securely manage your API keys for programmatic access.',
  category: 'settings',
  lastUpdated: '2025-06-29',
  tags: ['api-keys', 'security', 'authentication'],
  content: () => (
    <div>
      <div className="space-y-6">
        <p className="text-muted-foreground/90 leading-relaxed">
          API keys allow you to access the compression service programmatically. They provide 
          secure authentication for your applications and scripts.
        </p>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Creating an API Key</h2>
          <p className="text-muted-foreground/90 leading-relaxed mb-3">
            To create a new API key from your dashboard:
          </p>
          <ol className="list-decimal list-inside text-muted-foreground/90 space-y-2 ml-4">
            <li>Navigate to your dashboard and click "API Keys" in the sidebar</li>
            <li>In the "Create new API key" section, enter a descriptive name for your key (e.g., "my script key")</li>
            <li>Click the "Create key" button</li>
            <li>Copy the generated key immediately - you won't be able to see it again!</li>
            <li>Store it securely in your application using environment variables</li>
          </ol>
          <div className="bg-warning/10 border border-warning/30 rounded-xl p-4 mt-4">
            <p className="text-warning">
              <strong>Important:</strong> The API key is only shown once when created. Make sure to copy and store it immediately!
            </p>
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Your API Keys Table</h2>
          <p className="text-muted-foreground/90 leading-relaxed mb-3">
            The "Your API keys" section shows all your keys in a table with the following information:
          </p>
          <ul className="list-disc list-inside text-muted-foreground/90 space-y-2 ml-4">
            <li><strong>Name:</strong> The descriptive name you gave the key</li>
            <li><strong>Created:</strong> When the key was created (in YYYY-MM-DD HH:MM:SS format)</li>
            <li><strong>Last Used:</strong> When the key was last used for an API call (shows "Never" if unused)</li>
            <li><strong>Status:</strong> Shows "Active" (green) or "Revoked" (red)</li>
            <li><strong>Actions:</strong> "Revoke" button for active keys</li>
          </ul>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Revoking API Keys</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            If a key is compromised or no longer needed:
          </p>
          <ol className="list-decimal list-inside text-muted-foreground space-y-2 ml-4">
            <li>Navigate to your API Keys page</li>
            <li>Find the key you want to revoke in the table</li>
            <li>Click the "Revoke" button in the Actions column</li>
            <li>Confirm the action when prompted</li>
          </ol>
          <div className="bg-danger-soft border border-danger-border rounded-lg p-4 mt-4">
            <p className="text-danger-muted">
              <strong>Warning:</strong> Revoked keys cannot be restored. You'll need to create a new key if needed.
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Security Best Practices</h2>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Never share your API keys publicly or commit them to version control</li>
            <li>Use environment variables to store keys in your applications</li>
            <li>Create separate keys for different applications or environments</li>
            <li>Regularly review and revoke unused keys</li>
            <li>Monitor key usage in your dashboard</li>
            <li>If you suspect a key is compromised, revoke it immediately</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Using Your API Keys</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            Once you have an API key, you can use it to authenticate API requests. For detailed information 
            about endpoints, request formats, and code examples, check out the <a href="/docs/api" className="text-accent hover:text-accent/90">API documentation</a>.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Need Help?</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you're having trouble with API keys or need assistance with programmatic access, 
            feel free to contact <a href="mailto:stan@compress-glb.com" className="text-accent hover:text-accent/90">stan@compress-glb.com</a>.
          </p>
        </div>
      </div>
    </div>
  )
}; 